import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Alert, Box, Button, CircularProgress, Snackbar, Tooltip } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { SectionExportBuilderCtx, SectionExportCtx, type Builder } from '../hooks/useSectionExport';
import { useAuth } from '../auth/useAuth';
import { colors } from '../theme';
import { downloadExcel, type ExportAbout, type ExportResult } from '../util/excel-export';

const AboutCtx = createContext<ExportAbout | undefined>(undefined);

interface SectionExportProviderProps {
  children: ReactNode;
  /** What this section is — printed on the Export Info sheet. Sourced from
   *  the User Guide catalogs so the two never drift apart. */
  about?: ExportAbout;
}

/** One export slot per dashboard section — see `hooks/useSectionExport`. */
export function SectionExportProvider({ children, about }: SectionExportProviderProps) {
  const [builder, setBuilderState] = useState<Builder | null>(null);
  // Wrap: passing a function straight to setState would invoke it.
  const setBuilder = useCallback((b: Builder | null) => setBuilderState(() => b), []);
  const ctx = useMemo(() => ({ setBuilder }), [setBuilder]);
  return (
    <SectionExportCtx.Provider value={ctx}>
      <SectionExportBuilderCtx.Provider value={builder}>
        <AboutCtx.Provider value={about}>{children}</AboutCtx.Provider>
      </SectionExportBuilderCtx.Provider>
    </SectionExportCtx.Provider>
  );
}

interface SectionExportButtonProps {
  /** Hover / focus / progress colour — pass the surface's accent (the
   *  section's category colour, or the Field page's gold). */
  accent?: string;
}

/**
 * "Export" button. Renders nothing until the open section registers a builder.
 *
 * Responsive: icon-only below `sm` (the label would crowd a phone-width
 * header or tab bar) with a 36px square touch target; icon + "Export"
 * from `sm` up. The tooltip uses the app's light card style.
 */
export function SectionExportButton({ accent = colors.chartPrimary }: SectionExportButtonProps) {
  const builder = useContext(SectionExportBuilderCtx);
  const about = useContext(AboutCtx);
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ExportResult | null>(null);

  if (!builder) return null;

  const onClick = async (e: MouseEvent) => {
    // The header row toggles the section; don't collapse it on click.
    e.stopPropagation();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const spec = await builder();
      if (spec) {
        const name = user ? `${user.first_name} ${user.last_name}`.trim() : '';
        setDone(
          await downloadExcel(
            { ...spec, about: spec.about ?? about },
            { name: name || 'User', email: user?.email },
          ),
        );
      }
    } catch {
      setError('Export failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const tip =
    error ??
    (busy ? 'Preparing your file…' : 'Export what this section is showing to Excel (.xlsx)');

  return (
    <>
      <Tooltip
        title={tip}
        placement="bottom-end"
        arrow
        enterDelay={300}
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: '#fff',
              color: error ? colors.error : colors.textPrimary,
              border: `1px solid ${error ? `${colors.error}55` : colors.border}`,
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.4,
              maxWidth: 240,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            },
          },
          arrow: {
            sx: {
              color: '#fff',
              '&::before': { border: `1px solid ${error ? `${colors.error}55` : colors.border}` },
            },
          },
        }}
      >
        {/* Span wrapper: a disabled button fires no events, so the tooltip
            would otherwise go silent while exporting. */}
        <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onClick}
            onMouseDown={(e) => e.stopPropagation()}
            // Enter / Space would bubble to the header and toggle the section.
            onKeyDown={(e) => e.stopPropagation()}
            disabled={busy}
            aria-label="Export to Excel"
            startIcon={
              busy ? (
                <CircularProgress size={14} sx={{ color: accent }} />
              ) : (
                <FileDownloadOutlinedIcon sx={{ fontSize: '18px !important' }} />
              )
            }
            sx={{
              textTransform: 'none',
              fontSize: 12.5,
              fontWeight: 600,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              borderRadius: 1.5,
              height: { xs: 36, sm: 32 },
              minWidth: { xs: 36, sm: 0 },
              px: { xs: 0, sm: 1.25 },
              color: error ? colors.error : colors.textSecondary,
              borderColor: error ? colors.error : colors.border,
              bgcolor: '#fff',
              transition: 'color 160ms ease, border-color 160ms ease, background-color 160ms ease',
              '& .MuiButton-startIcon': { m: { xs: 0, sm: '0 6px 0 -2px' } },
              '& .label': { display: { xs: 'none', sm: 'inline' } },
              '&:hover': { borderColor: accent, color: accent, bgcolor: `${accent}0d` },
              '&.Mui-focusVisible': { outline: `2px solid ${accent}66`, outlineOffset: 1 },
              '&.Mui-disabled': {
                color: colors.textMuted,
                borderColor: colors.border,
                bgcolor: '#fff',
              },
            }}
          >
            <span className="label">{busy ? 'Exporting…' : 'Export'}</span>
          </Button>
        </Box>
      </Tooltip>

      {/* React events bubble out of portals, so stop clicks here from
          reaching the section header and toggling it. */}
      <Snackbar
        open={!!done}
        autoHideDuration={4000}
        onClose={() => setDone(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Alert
          severity="success"
          variant="outlined"
          onClose={() => setDone(null)}
          sx={{
            bgcolor: '#fff',
            color: colors.textPrimary,
            borderColor: `${colors.success}55`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06)',
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { sm: 420 },
            alignItems: 'flex-start',
            '& .MuiAlert-message': { minWidth: 0 },
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, fontSize: 13 }}>
            Exported {done?.rowCount.toLocaleString()} rows
          </Box>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: 12,
              color: colors.textMuted,
              mt: 0.25,
              overflowWrap: 'anywhere',
            }}
          >
            {done?.filename}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}
